/**
 * GameScreen - Main gameplay screen
 * 
 * FIXES:
 * - "Are you ready?" only shows at the START of game, not between levels
 * - Better speech bubble positioning
 * - Stricter word matching
 */

import { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { redact } from '../../../engine/language.js';
import KitchenScene from '../scenes/KitchenScene';
import PipCharacter from '../characters/PipCharacter';
import ConfettiSystem from '../effects/ConfettiSystem';
import './GameScreen.css';

const GameScreen = forwardRef(({
  level,
  levelNumber,
  totalLevels,
  stars,
  isFirstLevel,  // NEW: only show ready overlay on first level
  isListening,
  isSpeaking,
  transcript,
  settings,
  onReady,
  onHome,
  onToggleMic,
  onLevelComplete,
  speakAsPip,
  speakPraise,
  speak,
  playSound,
}, ref) => {
  // Scene state
  const [pipPosition, setPipPosition] = useState('center');
  const [pipMood, setPipMood] = useState('happy');
  const [pipAnimation, setPipAnimation] = useState('idle');
  const [pipHolding, setPipHolding] = useState(null);
  const [pipBubble, setPipBubble] = useState('');
  const [showReadyOverlay, setShowReadyOverlay] = useState(isFirstLevel);
  
  // Kitchen state
  const [fridgeOpen, setFridgeOpen] = useState(false);
  const [highlightItem, setHighlightItem] = useState(null);
  
  // UI state
  const [feedback, setFeedback] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [heardText, setHeardText] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  // Refs
  const pipRef = useRef(null);
  const kitchenRef = useRef(null);

  // Praise phrases
  const praiseRef = useRef([
    "Amazing!", "Great job!", "You did it!", "Fantastic!", "Wonderful!",
    "Super!", "Excellent!", "Perfect!", "Brilliant!", "Awesome!",
    "Way to go!", "You're a star!", "Incredible!", "Outstanding!",
    "Superb!", "Terrific!", "Marvelous!", "Spectacular!", "Phenomenal!",
    "You rock!", "Nailed it!", "Beautiful!", "Well done!", "Bravo!",
    "Impressive!", "Exceptional!", "Sensational!", "Fabulous!",
    "You're the best!", "Keep it up!", "That's right!", "Yes!",
    "Woohoo!", "Yay!", "You got it!", "Gold star!", "High five!",
    "So proud!", "Exactly!", "Spot on!", "Pip is happy!", "So smart!",
  ]);
  const usedPraiseRef = useRef([]);

  // Get random praise (no repeats until all used)
  const getPraise = useCallback(() => {
    if (usedPraiseRef.current.length >= praiseRef.current.length) {
      usedPraiseRef.current = [];
    }
    const available = praiseRef.current.filter(p => !usedPraiseRef.current.includes(p));
    const praise = available[Math.floor(Math.random() * available.length)];
    usedPraiseRef.current.push(praise);
    return praise;
  }, []);

  // Setup level - NO ready overlay between levels
  useEffect(() => {
    if (!level) return;
    
    // Reset state
    setFeedback(null);
    setPipBubble('');
    setHeardText('');
    setShowConfetti(false);
    setIsBusy(false);
    
    // Apply scene setup
    const setup = level.sceneSetup || {};
    setPipPosition(setup.pipPosition || 'center');
    setFridgeOpen(setup.fridgeOpen || false);
    setHighlightItem(setup.highlightItem || null);
    setPipHolding(setup.pipHolding || null);
    setPipMood('happy');
    setPipAnimation('idle');
    
    // Only show ready overlay on FIRST level
    if (isFirstLevel) {
      setShowReadyOverlay(true);
    } else {
      // Auto-announce the word for subsequent levels
      if (settings.voiceEnabled) {
        setTimeout(() => {
          speak(`Say ${level.word.toUpperCase()}!`);
        }, 800);
      }
    }
    
    // Pip speaks introduction (only if not showing ready overlay)
    if (!isFirstLevel && level.pipSays && settings.voiceEnabled) {
      setTimeout(() => {
        setPipBubble(level.pipSays);
        speakAsPip(level.pipSays);
        setTimeout(() => setPipBubble(''), 3500);
      }, 300);
    }
  }, [level, isFirstLevel, settings.voiceEnabled, speakAsPip, speak]);

  // Update heard text from transcript.
  // Redacted: the recogniser returns the nearest dictionary word, and for a
  // child's imprecise speech that is sometimes an obscenity. It must never be
  // drawn on screen attributed to them. See engine/language.js.
  useEffect(() => {
    if (transcript) {
      setHeardText(`Heard: "${redact(transcript)}"`);
    }
  }, [transcript]);

  // Handle ready button
  const handleReady = useCallback(() => {
    setShowReadyOverlay(false);
    onReady();
    
    // Pip intro after ready
    if (level.pipSays && settings.voiceEnabled) {
      setTimeout(() => {
        setPipBubble(level.pipSays);
        speakAsPip(level.pipSays);
        setTimeout(() => setPipBubble(''), 3500);
      }, 500);
    }
  }, [onReady, level, settings.voiceEnabled, speakAsPip]);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    performAction: (action) => {
      if (!isBusy) {
        setIsBusy(true);
        executeAction(action);
      }
    },
    showHint: (text) => {
      setFeedback({ type: 'hint', text });
      setTimeout(() => setFeedback(null), 2500);
    },
  }));

  // Execute level action
  const executeAction = async (action) => {
    // Show praise immediately
    const praise = getPraise();
    setFeedback({ type: 'success', text: praise });
    
    if (settings.voiceEnabled) {
      speakPraise(praise);
    }

    // Celebrate
    setPipMood('excited');
    setPipAnimation('celebrate');

    // Wait a moment then do the action
    await delay(500);

    switch (action) {
      case 'openFridge':
        setPipPosition('fridge');
        await delay(400);
        setFridgeOpen(true);
        if (settings.soundEffects) playSound.playFridge();
        await delay(800);
        break;

      case 'getJuice':
        setHighlightItem(null);
        setPipHolding('juice');
        if (settings.soundEffects) playSound.playPop();
        await delay(600);
        break;

      case 'drink':
        setPipAnimation('drink');
        if (settings.soundEffects) playSound.playGulp();
        await delay(1200);
        setPipHolding(null);
        break;

      case 'askMore':
        setPipBubble('More please!');
        speakAsPip('More please!');
        await delay(2000);
        setPipBubble('');
        break;

      case 'getApple':
        setHighlightItem(null);
        setPipHolding('apple');
        if (settings.soundEffects) playSound.playPop();
        await delay(600);
        break;

      case 'eat':
        setPipAnimation('eat');
        if (settings.soundEffects) playSound.playMunch();
        await delay(1200);
        setPipHolding(null);
        break;

      case 'sayYum':
        setPipBubble('YUM YUM YUM!');
        speakAsPip('Yum yum yum!');
        setPipAnimation('celebrate');
        await delay(2000);
        setPipBubble('');
        break;

      case 'closeFridge':
        setPipPosition('fridge');
        await delay(400);
        setFridgeOpen(false);
        if (settings.soundEffects) playSound.playFridge();
        await delay(600);
        break;

      case 'beHappy':
        setPipPosition('center');
        setPipAnimation('celebrate');
        setPipBubble("I'm so happy!");
        speakAsPip('I am so happy!');
        await delay(2000);
        setPipBubble('');
        break;

      case 'sayBye':
        setPipBubble('Bye bye!');
        speakAsPip('Bye bye! Thanks for playing with me!');
        setPipAnimation('wave');
        await delay(2500);
        setPipBubble('');
        break;

      default:
        await delay(500);
    }

    // Show confetti and complete
    setShowConfetti(true);
    setFeedback(null);
    setPipMood('happy');
    setPipAnimation('idle');

    await delay(500);
    setIsBusy(false);
    onLevelComplete();
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Check if game is active (not showing overlay)
  const isActive = !showReadyOverlay && !isBusy;

  return (
    <div className="game-screen">
      {/* Kitchen Scene */}
      <div className="game-scene">
        <KitchenScene
          ref={kitchenRef}
          fridgeOpen={fridgeOpen}
          highlightItem={highlightItem}
        />

        {/* Pip Character */}
        <div className={`pip-container pip-container--${pipPosition}`}>
          <PipCharacter
            ref={pipRef}
            mood={pipMood}
            animation={pipAnimation}
            holding={pipHolding}
            bubble={pipBubble}
          />
        </div>

        {/* HUD */}
        <div className="game-hud">
          <div className="hud-left">
            <div className="hud-stars">
              <span className="hud-stars__icon">⭐</span>
              <span className="hud-stars__count">{stars}</span>
            </div>
          </div>

          <div className="hud-center">
            <div className="hud-progress">
              <div 
                className="hud-progress__fill"
                style={{ width: `${(levelNumber / totalLevels) * 100}%` }}
              />
            </div>
          </div>

          <div className="hud-right">
            <div className="hud-level">Level {levelNumber}</div>
            <button className="hud-home" onClick={onHome} aria-label="Home">
              🏠
            </button>
          </div>
        </div>

        {/* Confetti */}
        {showConfetti && <ConfettiSystem />}
      </div>

      {/* Controls */}
      <div className="game-controls">
        <div className="controls-prompt">
          <div className="prompt-label">Say this word:</div>
          <div className="prompt-word">{level.word.toUpperCase()}</div>
          <div className="prompt-hint">{level.instruction}</div>
        </div>

        <div className="controls-mic">
          <button
            className={`mic-btn ${isListening ? 'mic-btn--listening' : ''} ${!isActive ? 'mic-btn--disabled' : ''}`}
            onClick={onToggleMic}
            disabled={!isActive}
          >
            <span className="mic-btn__icon">{isListening ? '🎤' : '🔇'}</span>
            {isListening && <span className="mic-btn__pulse" />}
          </button>
        </div>

        <div className="controls-transcript">
          {heardText || (isListening ? 'Listening...' : 'Tap 🎤 to speak')}
        </div>
      </div>

      {/* Feedback Toast */}
      {feedback && (
        <div className={`feedback feedback--${feedback.type}`}>
          {feedback.text}
        </div>
      )}

      {/* Ready Overlay - ONLY on first level */}
      {showReadyOverlay && (
        <div className="ready-overlay">
          <div className="ready-content">
            <div className="ready-level">Level {levelNumber}</div>
            <div className="ready-word">{level.word.toUpperCase()}</div>
            <button className="ready-btn" onClick={handleReady}>
              Are you ready?
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

GameScreen.displayName = 'GameScreen';

export default GameScreen;
